import { useRef, useCallback, useState, useEffect } from 'react';
import { useLanguage } from '../../i18n';
import type { InputMode, TestCaseAnalysis, QAOption } from './types';
import { isTestCaseFileValid, generateDisplayFileName } from './types';

// 파일 미리보기 모달 타입
interface FilePreviewState {
  isOpen: boolean;
  fileName: string;
  fileType: 'csv' | 'text' | 'pdf' | 'image' | 'unknown';
  content: string | null;
  blobUrl: string | null;
}

// CSV 파싱 함수
const parseCsv = (csvText: string): string[][] => {
  const rows: string[][] = [];
  let currentRow: string[] = [];
  let currentCell = '';
  let inQuotes = false;
  
  for (let i = 0; i < csvText.length; i++) {
    const char = csvText[i];
    const nextChar = csvText[i + 1];
    
    if (inQuotes) {
      if (char === '"' && nextChar === '"') {
        currentCell += '"';
        i++;
      } else if (char === '"') {
        inQuotes = false;
      } else {
        currentCell += char;
      }
    } else {
      if (char === '"') {
        inQuotes = true;
      } else if (char === ',') {
        currentRow.push(currentCell);
        currentCell = '';
      } else if (char === '\n' || (char === '\r' && nextChar === '\n')) {
        currentRow.push(currentCell);
        rows.push(currentRow);
        currentRow = [];
        currentCell = '';
        if (char === '\r') i++;
      } else if (char !== '\r') {
        currentCell += char;
      }
    }
  }
  
  if (currentCell || currentRow.length > 0) {
    currentRow.push(currentCell);
    rows.push(currentRow);
  }
  
  return rows;
};

// 파일 확장자에 따른 미리보기 타입 결정
const getFilePreviewType = (fileName: string): 'csv' | 'text' | 'pdf' | 'image' | 'unknown' => {
  const ext = fileName.toLowerCase().split('.').pop() || '';
  if (ext === 'csv') return 'csv';
  if (['txt', 'md', 'markdown', 'json', 'xml', 'html', 'log'].includes(ext)) return 'text';
  if (ext === 'pdf') return 'pdf';
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp'].includes(ext)) return 'image';
  return 'unknown';
};

// 커스텀 드롭다운 컴포넌트
interface CustomSelectProps {
  onChange: (value: string) => void;
  options: QAOption[];
  placeholder: string;
  disabled?: boolean;
  testId?: string;
  isSelected: (value: string) => boolean;
}

function CustomSelect({ onChange, options, placeholder, disabled, testId, isSelected }: CustomSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // 선택된 옵션의 라벨 찾기
  const selectedOption = options.find(opt => isSelected(opt.value));
  const displayLabel = selectedOption?.label || placeholder;

  // 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={ref} className="relative" data-testid={testId}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`w-full px-3 py-1.5 text-left text-sm rounded border transition-colors flex items-center justify-between ${
          disabled ? 'cursor-not-allowed bg-terminal-input/60' : 'cursor-pointer hover:border-terminal-dim'
        } ${
          selectedOption
            ? 'border-status-go/50 text-status-go bg-status-go/10'
            : 'border-terminal-border text-terminal-muted'
        }`}
      >
        <span>{displayLabel}</span>
        <svg
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isOpen && !disabled && (
        <div className="absolute z-10 w-full mt-1 bg-terminal-bg border border-terminal-border rounded shadow-lg">
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => {
                onChange(opt.value);
                setIsOpen(false);
              }}
              className={`w-full px-3 py-1.5 text-left text-sm hover:bg-terminal-input transition-colors ${
                isSelected(opt.value)
                  ? 'text-status-go bg-status-go/10'
                  : 'text-terminal-text'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

interface QAStatusSectionProps {
  qaCore: string;
  setQaCore: (value: string) => void;
  qaEdge: string;
  setQaEdge: (value: string) => void;
  qaInputMode: InputMode;
  setQaInputMode: (mode: InputMode) => void;
  testCaseFiles: File[];
  setTestCaseFiles: (files: File[]) => void;
  testCaseAnalysis: TestCaseAnalysis | null;
  isAnalyzing: boolean;
  analyzeTestCaseFiles: (files: File[]) => Promise<void>;
  clearTestCaseAnalysis: () => void;
  showFileError: (message: string) => void;
  testCaseLimit?: number;
  testCaseFileLimit?: number;
  isDemo?: boolean;
  coreOptions?: QAOption[];
  edgeOptions?: QAOption[];
  featureName?: string;
  isFreePlan?: boolean;
}

function QAStatusSection({
  qaCore,
  setQaCore,
  qaEdge,
  setQaEdge,
  qaInputMode,
  setQaInputMode,
  testCaseFiles,
  setTestCaseFiles,
  testCaseAnalysis,
  isAnalyzing,
  analyzeTestCaseFiles,
  clearTestCaseAnalysis,
  showFileError,
  testCaseLimit = 10,
  testCaseFileLimit = 1,
  isDemo = false,
  coreOptions,
  edgeOptions,
  featureName = '',
  isFreePlan = false,
}: QAStatusSectionProps) {
  const { lang, t } = useLanguage();

  // 기본 옵션 (props로 전달되지 않으면 사용)
  const defaultCoreOptions: QAOption[] = [
    { value: t.qaValue.verified, label: t.form.passVerified },
    { value: t.qaValue.partiallyVerified, label: t.form.partialVerified },
    { value: t.qaValue.unverified, label: t.form.failUnverified },
  ];

  const defaultEdgeOptions: QAOption[] = [
    { value: t.qaValue.verified, label: t.form.passVerified },
    { value: t.qaValue.partiallyVerified, label: t.form.partialVerified },
    { value: t.qaValue.unverified, label: t.form.failUnverified },
  ];

  const finalCoreOptions = coreOptions ?? defaultCoreOptions;
  const finalEdgeOptions = edgeOptions ?? defaultEdgeOptions;
  const testCaseFileRef = useRef<HTMLInputElement>(null);

  // 파일 미리보기 상태
  const [previewState, setPreviewState] = useState<FilePreviewState>({
    isOpen: false,
    fileName: '',
    fileType: 'unknown',
    content: null,
    blobUrl: null,
  });

  // 파일 미리보기 핸들러
  const handlePreviewFile = useCallback(async (file: File) => {
    const fileType = getFilePreviewType(file.name);
    
    if (fileType === 'unknown') {
      showFileError(lang === 'ko' ? '이 파일 형식은 미리보기를 지원하지 않습니다.' : 'Preview is not supported for this file type.');
      return;
    }

    if (fileType === 'csv' || fileType === 'text') {
      const text = await file.text();
      setPreviewState({
        isOpen: true,
        fileName: file.name,
        fileType,
        content: text,
        blobUrl: null,
      });
    } else {
      const blobUrl = URL.createObjectURL(file);
      setPreviewState({
        isOpen: true,
        fileName: file.name,
        fileType,
        content: null,
        blobUrl,
      });
    }
  }, [lang, showFileError]);

  // 미리보기 닫기
  const closePreview = useCallback(() => {
    if (previewState.blobUrl) {
      URL.revokeObjectURL(previewState.blobUrl);
    }
    setPreviewState({
      isOpen: false,
      fileName: '',
      fileType: 'unknown',
      content: null,
      blobUrl: null,
    });
  }, [previewState.blobUrl]);

  // 테스트 케이스 템플릿 생성 및 다운로드
  const downloadTestCaseTemplate = useCallback(() => {
    const csvContent =
      lang === 'ko'
        ? `우선순위,TC ID,분류,테스트 설명,사전 조건,테스트 단계,예상 결과,테스터,실제 결과,테스트 일자,결과 설명,비고
High,TC001,core,로그인 성공 테스트,사용자 계정 존재,1. 로그인 페이지 접속 2. 아이디/비밀번호 입력 3. 로그인 버튼 클릭,메인 화면 이동,홍길동,passed,2024-01-15,정상 동작 확인,
High,TC002,core,로그인 실패 - 잘못된 비밀번호,사용자 계정 존재,1. 로그인 페이지 접속 2. 잘못된 비밀번호 입력 3. 로그인 버튼 클릭,에러 메시지 표시,홍길동,passed,2024-01-15,에러 메시지 정상 표시,
High,TC003,core,회원가입 성공,신규 이메일 필요,1. 회원가입 페이지 접속 2. 필수 정보 입력 3. 가입 버튼 클릭,가입 완료 및 로그인,홍길동,passed,2024-01-15,이메일 인증 포함,
Medium,TC004,core,비밀번호 찾기,등록된 이메일 존재,1. 비밀번호 찾기 클릭 2. 이메일 입력 3. 전송 버튼 클릭,재설정 이메일 발송,김철수,passed,2024-01-16,메일 수신 확인,
Medium,TC005,edge,세션 만료 처리,로그인 상태,1. 로그인 후 30분 대기 2. 페이지 새로고침,자동 로그아웃 및 로그인 페이지 이동,김철수,failed,2024-01-16,세션 만료 후 에러 발생,세션 타이머 로직 확인 필요
Low,TC006,edge,동시 로그인 제한,동일 계정 다른 기기 접속,1. A기기 로그인 2. B기기 동일 계정 로그인,A기기 자동 로그아웃,김철수,failed,2024-01-16,로그아웃 미발생,추가 개발 필요`
        : `Priority,TC ID,Category,Test Description,Pre-Conditions,Test Step,Expected Result,Tester,Actual Result,Test Date,Description about result,Comment
High,TC001,core,Login Success Test,User account exists,1. Go to login page 2. Enter ID/password 3. Click login,Navigate to main screen,John Doe,passed,2024-01-15,Working correctly,
High,TC002,core,Login Failure - Wrong Password,User account exists,1. Go to login page 2. Enter wrong password 3. Click login,Show error message,John Doe,passed,2024-01-15,Error message displayed correctly,
High,TC003,core,Signup Success,New email required,1. Go to signup page 2. Enter required info 3. Click signup,Complete signup and login,John Doe,passed,2024-01-15,Includes email verification,
Medium,TC004,core,Forgot Password,Registered email exists,1. Click forgot password 2. Enter email 3. Click send,Send reset email,Jane Smith,passed,2024-01-16,Email received,
Medium,TC005,edge,Session Expiry,Logged in state,1. Login and wait 30 min 2. Refresh page,Auto logout and redirect to login,Jane Smith,failed,2024-01-16,Error after session expiry,Check session timer logic
Low,TC006,edge,Concurrent Login Limit,Same account on different devices,1. Login on device A 2. Login on device B with same account,Auto logout on device A,Jane Smith,failed,2024-01-16,No logout occurred,Additional development needed`;

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = lang === 'ko' ? 'test_cases_template_ko.csv' : 'test_cases_template_en.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [lang]);

  const handleTestCaseFileDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const droppedFiles = Array.from(e.dataTransfer.files);

      if (droppedFiles.length === 0) return;

      const validFiles: File[] = [];
      const invalidFiles: string[] = [];

      droppedFiles.forEach((file) => {
        if (isTestCaseFileValid(file)) {
          validFiles.push(file);
        } else {
          invalidFiles.push(file.name);
        }
      });

      if (invalidFiles.length > 0) {
        showFileError(
          lang === 'ko'
            ? `⚠️ 테스트 케이스는 CSV, JSON, MD 파일만 지원합니다.\n지원하지 않는 파일: ${invalidFiles.join(', ')}`
            : `⚠️ Test cases only support CSV, JSON, MD files.\nUnsupported: ${invalidFiles.join(', ')}`
        );
      }

      if (validFiles.length === 0) return;

      // 파일 개수 제한 체크
      const newFiles = [...testCaseFiles, ...validFiles];
      if (newFiles.length > testCaseFileLimit) {
        showFileError(
          lang === 'ko'
            ? `⚠️ 테스트 케이스 파일은 최대 ${testCaseFileLimit}개까지 첨부 가능합니다. (현재 플랜 기준)`
            : `⚠️ You can attach up to ${testCaseFileLimit} test case files. (Based on your plan)`
        );
        const limitedFiles = newFiles.slice(0, testCaseFileLimit);
        setTestCaseFiles(limitedFiles);
        analyzeTestCaseFiles(limitedFiles);
      } else {
        setTestCaseFiles(newFiles);
        analyzeTestCaseFiles(newFiles);
      }
    },
    [analyzeTestCaseFiles, showFileError, lang, setTestCaseFiles, testCaseFiles, testCaseFileLimit]
  );

  const handleTestCaseFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFiles = Array.from(e.target.files || []);

      if (selectedFiles.length === 0) return;

      const validFiles: File[] = [];
      const invalidFiles: string[] = [];

      selectedFiles.forEach((file) => {
        if (isTestCaseFileValid(file)) {
          validFiles.push(file);
        } else {
          invalidFiles.push(file.name);
        }
      });

      if (invalidFiles.length > 0) {
        showFileError(
          lang === 'ko'
            ? `⚠️ 테스트 케이스는 CSV, JSON, MD 파일만 지원합니다.\n지원하지 않는 파일: ${invalidFiles.join(', ')}`
            : `⚠️ Test cases only support CSV, JSON, MD files.\nUnsupported: ${invalidFiles.join(', ')}`
        );
      }

      if (testCaseFileRef.current) testCaseFileRef.current.value = '';

      if (validFiles.length === 0) return;

      // 파일 개수 제한 체크
      const newFiles = [...testCaseFiles, ...validFiles];
      if (newFiles.length > testCaseFileLimit) {
        showFileError(
          lang === 'ko'
            ? `⚠️ 테스트 케이스 파일은 최대 ${testCaseFileLimit}개까지 첨부 가능합니다. (현재 플랜 기준)`
            : `⚠️ You can attach up to ${testCaseFileLimit} test case files. (Based on your plan)`
        );
        const limitedFiles = newFiles.slice(0, testCaseFileLimit);
        setTestCaseFiles(limitedFiles);
        analyzeTestCaseFiles(limitedFiles);
      } else {
        setTestCaseFiles(newFiles);
        analyzeTestCaseFiles(newFiles);
      }
    },
    [analyzeTestCaseFiles, showFileError, lang, setTestCaseFiles, testCaseFiles, testCaseFileLimit]
  );

  const clearTestCaseFiles = useCallback(() => {
    setTestCaseFiles([]);
    clearTestCaseAnalysis();
    setQaCore('');
    setQaEdge('');
    if (testCaseFileRef.current) testCaseFileRef.current.value = '';
  }, [clearTestCaseAnalysis, setQaCore, setQaEdge, setTestCaseFiles]);

  const removeTestCaseFile = useCallback(
    (index: number) => {
      const newFiles = testCaseFiles.filter((_, i) => i !== index);
      setTestCaseFiles(newFiles);
      if (newFiles.length === 0) {
        clearTestCaseAnalysis();
        setQaCore('');
        setQaEdge('');
      } else {
        analyzeTestCaseFiles(newFiles);
      }
    },
    [testCaseFiles, setTestCaseFiles, clearTestCaseAnalysis, setQaCore, setQaEdge, analyzeTestCaseFiles]
  );

  // 데모 데이터는 "(15/15)" 같은 추가 정보를 포함할 수 있으므로 includes로 체크
  const isCoreSelected = (optValue: string) => qaCore === optValue || qaCore.includes(optValue);
  const isEdgeSelected = (optValue: string) => qaEdge === optValue || qaEdge.includes(optValue);

  return (
    <div className="card p-2" data-testid="qa-status-section">
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-terminal-muted text-xs uppercase tracking-wide">
          # {t.form.qaStatus}
        </div>
        {/* 입력 모드 토글 - 데모 모드에서는 숨김 */}
        {!isDemo && (
          <div className="flex items-center gap-1.5 text-xs">
            <button
              type="button"
              onClick={() => {
                setQaInputMode('manual');
                clearTestCaseFiles();
              }}
              className={`px-2 py-0.5 rounded transition-colors ${
                qaInputMode === 'manual'
                  ? 'bg-status-go/20 text-status-go border border-status-go/30'
                  : 'text-terminal-muted hover:text-terminal-text'
              }`}
            >
              {t.form.manualInput}
            </button>
            {isFreePlan ? (
              <div 
                className="px-2 py-0.5 rounded text-terminal-dim cursor-not-allowed flex items-center gap-1 opacity-60"
                title={lang === 'ko' ? 'Pro 이상 플랜에서 사용 가능' : 'Available for Pro plan and above'}
              >
                <span>🔒</span>
                <span>{t.form.testCaseImport}</span>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setQaInputMode('file')}
                className={`px-2 py-0.5 rounded transition-colors ${
                  qaInputMode === 'file'
                    ? 'bg-status-go/20 text-status-go border border-status-go/30'
                    : 'text-terminal-muted hover:text-terminal-text'
                }`}
              >
                {t.form.testCaseImport}
              </button>
            )}
          </div>
        )}
      </div>

      {qaInputMode === 'manual' || isDemo ? (
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-terminal-dim text-xs mb-1">{t.form.coreScenario}</label>
            <CustomSelect
              onChange={setQaCore}
              options={finalCoreOptions}
              placeholder={t.form.select}
              disabled={isDemo}
              testId="select-qa-core"
              isSelected={isCoreSelected}
            />
          </div>
          <div>
            <label className="block text-terminal-dim text-xs mb-1">{t.form.edgeCases}</label>
            <CustomSelect
              onChange={setQaEdge}
              options={finalEdgeOptions}
              placeholder={t.form.select}
              disabled={isDemo}
              testId="select-qa-edge"
              isSelected={isEdgeSelected}
            />
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {/* 템플릿 다운로드 */}
          <div className="flex items-center justify-between bg-terminal-input/50 rounded p-3 border border-terminal-border">
            <div className="text-sm">
              <div className="text-terminal-text font-medium mb-0.5">
                {t.form.downloadTemplate}
              </div>
              <div className="text-terminal-dim text-xs">{t.form.templateDescription}</div>
            </div>
            <button
              type="button"
              onClick={downloadTestCaseTemplate}
              className="px-3 py-1.5 text-xs border border-status-go text-status-go rounded hover:bg-status-go/10 transition-colors whitespace-nowrap"
            >
              {t.form.downloadTemplate}
            </button>
          </div>

          <div className="flex items-center gap-3 text-terminal-dim text-xs">
            <div className="flex-1 h-px bg-terminal-border"></div>
            <span>{t.form.orDivider}</span>
            <div className="flex-1 h-px bg-terminal-border"></div>
          </div>

          {/* 파일 드롭 영역 */}
          {testCaseFiles.length === 0 ? (
            <div
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleTestCaseFileDrop}
              onClick={() => testCaseFileRef.current?.click()}
              className="border-2 border-dashed border-terminal-border rounded-lg p-6 text-center cursor-pointer hover:border-status-go/50 transition-colors"
            >
              <input
                ref={testCaseFileRef}
                type="file"
                accept=".csv,.json,.md"
                multiple={testCaseFileLimit > 1}
                onChange={handleTestCaseFileChange}
                className="hidden"
              />
              <div className="text-terminal-muted text-sm mb-1">{t.form.testCaseDragDrop}</div>
              <div className="text-terminal-dim text-xs">{t.form.testCaseSupportedFormats}</div>
              {testCaseFileLimit > 1 && (
                <div className="text-terminal-dim text-xs mt-1">
                  {lang === 'ko' 
                    ? `(최대 ${testCaseFileLimit}개 파일 동시 첨부 가능)`
                    : `(Up to ${testCaseFileLimit} files at once)`}
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {/* 업로드된 파일 목록 */}
              <div className="space-y-2">
                {testCaseFiles.map((file, index) => {
                  const displayName = generateDisplayFileName(featureName, 'testcase', index, file.name);
                  return (
                    <div key={`${file.name}-${index}`} className="flex items-center justify-between bg-terminal-input rounded p-2">
                      <div 
                        className="flex items-center gap-2 text-sm cursor-pointer hover:opacity-80 flex-1 min-w-0"
                        onClick={() => handlePreviewFile(file)}
                        title={lang === 'ko' ? '클릭하여 미리보기' : 'Click to preview'}
                      >
                        <span>📎</span>
                        <span className="text-terminal-text truncate hover:text-status-go transition-colors">
                          {displayName}
                          <span className="ml-1 text-xs text-terminal-muted opacity-60">👁️</span>
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeTestCaseFile(index)}
                        className="text-xs text-status-danger hover:underline flex-shrink-0"
                      >
                        {t.form.clearFile}
                      </button>
                    </div>
                  );
                })}
              </div>
              
              {/* 추가 파일 업로드 (제한에 도달하지 않은 경우) */}
              {testCaseFiles.length < testCaseFileLimit && (
                <div
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={handleTestCaseFileDrop}
                  onClick={() => testCaseFileRef.current?.click()}
                  className="border border-dashed border-terminal-border rounded p-3 text-center cursor-pointer hover:border-status-go/50 transition-colors"
                >
                  <input
                    ref={testCaseFileRef}
                    type="file"
                    accept=".csv,.json,.md"
                    multiple={testCaseFileLimit > 1}
                    onChange={handleTestCaseFileChange}
                    className="hidden"
                  />
                  <div className="text-terminal-dim text-xs">
                    {lang === 'ko' 
                      ? `+ 파일 추가 (${testCaseFiles.length}/${testCaseFileLimit})`
                      : `+ Add file (${testCaseFiles.length}/${testCaseFileLimit})`}
                  </div>
                </div>
              )}
              
              {/* 모두 삭제 버튼 */}
              {testCaseFiles.length > 1 && (
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={clearTestCaseFiles}
                    className="text-xs text-status-danger hover:underline"
                  >
                    {lang === 'ko' ? '모두 삭제' : 'Clear all'}
                  </button>
                </div>
              )}

              {/* 분석 중 표시 */}
              {isAnalyzing && (
                <div className="flex items-center gap-2 text-terminal-muted text-sm">
                  <div className="w-4 h-4 border-2 border-status-go/30 border-t-status-go rounded-full animate-spin"></div>
                  <span>{t.form.analyzing}</span>
                </div>
              )}

              {/* 분석 결과 */}
              {testCaseAnalysis && !isAnalyzing && (
                <div className="bg-terminal-bg rounded p-3 border border-terminal-border">
                  <div className="text-terminal-dim text-xs mb-3 uppercase">
                    📊 {t.form.testCaseAnalysis}
                  </div>

                  {/* 파싱 경고 메시지 */}
                  {testCaseAnalysis.parseWarning && (
                    <div className="bg-status-warning/10 border border-status-warning/30 rounded p-2 mb-3 text-xs text-status-warning">
                      {testCaseAnalysis.parseWarning}
                    </div>
                  )}

                  {/* 전체 통계 */}
                  <div className="grid grid-cols-3 gap-3 mb-3">
                    <div className="text-center">
                      <div className={`text-2xl font-bold ${testCaseAnalysis.total > testCaseLimit ? 'text-status-danger' : 'text-terminal-text'}`}>
                        {testCaseAnalysis.total}
                      </div>
                      <div className="text-xs text-terminal-dim">
                        {t.form.totalTestCases}
                      </div>
                      {testCaseAnalysis.total > testCaseLimit && (
                        <div className="text-xs text-status-danger mt-1">
                          ⚠️ {lang === 'ko' ? `${testCaseLimit}개까지만 허용됩니다` : `Only up to ${testCaseLimit} allowed`}
                        </div>
                      )}
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-status-go">
                        {testCaseAnalysis.passed}
                      </div>
                      <div className="text-xs text-terminal-dim">{t.form.passed}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-status-danger">
                        {testCaseAnalysis.failed}
                      </div>
                      <div className="text-xs text-terminal-dim">Failed</div>
                    </div>
                  </div>

                  {/* 전체 진행률 바 (통과/실패 스택) */}
                  <div className="progress-bar mb-4 flex overflow-hidden">
                    <div
                      className="bg-status-go h-full"
                      style={{
                        width: `${(testCaseAnalysis.passed / testCaseAnalysis.total) * 100}%`,
                      }}
                    />
                    <div
                      className="bg-status-danger h-full"
                      style={{
                        width: `${(testCaseAnalysis.failed / testCaseAnalysis.total) * 100}%`,
                      }}
                    />
                  </div>

                  {/* Core/Edge 상세 분석 */}
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    {/* Core 시나리오 */}
                    <div className="bg-terminal-input/50 rounded p-2">
                      <div className="text-xs text-terminal-dim mb-1">{t.form.coreTestCases}</div>
                      {testCaseAnalysis.coreTotal !== undefined &&
                      testCaseAnalysis.coreTotal > 0 ? (
                        <>
                          <div className="flex items-baseline gap-2">
                            <span className="text-lg font-bold text-status-go">
                              {testCaseAnalysis.corePassed}
                            </span>
                            {(testCaseAnalysis.coreFailed || 0) > 0 && (
                              <span className="text-lg font-bold text-status-danger">
                                -{testCaseAnalysis.coreFailed}
                              </span>
                            )}
                            <span className="text-terminal-dim text-xs">
                              / {testCaseAnalysis.coreTotal}
                            </span>
                          </div>
                          {/* 통과/실패 스택 바 */}
                          <div className="progress-bar h-1.5 mt-1 flex overflow-hidden">
                            <div
                              className="bg-status-go h-full"
                              style={{
                                width: `${((testCaseAnalysis.corePassed || 0) / testCaseAnalysis.coreTotal) * 100}%`,
                              }}
                            />
                            <div
                              className="bg-status-danger h-full"
                              style={{
                                width: `${((testCaseAnalysis.coreFailed || 0) / testCaseAnalysis.coreTotal) * 100}%`,
                              }}
                            />
                          </div>
                        </>
                      ) : (
                        <span className="text-terminal-dim text-xs">-</span>
                      )}
                    </div>

                    {/* Edge 케이스 */}
                    <div className="bg-terminal-input/50 rounded p-2">
                      <div className="text-xs text-terminal-dim mb-1">{t.form.edgeTestCases}</div>
                      {testCaseAnalysis.edgeTotal !== undefined &&
                      testCaseAnalysis.edgeTotal > 0 ? (
                        <>
                          <div className="flex items-baseline gap-2">
                            <span className="text-lg font-bold text-status-go">
                              {testCaseAnalysis.edgePassed}
                            </span>
                            {(testCaseAnalysis.edgeFailed || 0) > 0 && (
                              <span className="text-lg font-bold text-status-danger">
                                -{testCaseAnalysis.edgeFailed}
                              </span>
                            )}
                            <span className="text-terminal-dim text-xs">
                              / {testCaseAnalysis.edgeTotal}
                            </span>
                          </div>
                          {/* 통과/실패 스택 바 */}
                          <div className="progress-bar h-1.5 mt-1 flex overflow-hidden">
                            <div
                              className="bg-status-go h-full"
                              style={{
                                width: `${((testCaseAnalysis.edgePassed || 0) / testCaseAnalysis.edgeTotal) * 100}%`,
                              }}
                            />
                            <div
                              className="bg-status-danger h-full"
                              style={{
                                width: `${((testCaseAnalysis.edgeFailed || 0) / testCaseAnalysis.edgeTotal) * 100}%`,
                              }}
                            />
                          </div>
                        </>
                      ) : (
                        <span className="text-terminal-dim text-xs">-</span>
                      )}
                    </div>
                  </div>

                  {/* 자동 설정된 상태 */}
                  <div className="space-y-2 text-sm pt-2 border-t border-terminal-border">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-terminal-muted whitespace-nowrap">
                        {t.form.coreScenario}:
                      </span>
                      <span
                        className={`text-right ${
                          testCaseAnalysis.coreStatusType === 'verified'
                            ? 'text-status-go'
                            : testCaseAnalysis.coreStatusType === 'failed'
                              ? 'text-status-danger'
                              : testCaseAnalysis.coreStatusType === 'partial'
                                ? 'text-status-warning'
                                : 'text-terminal-muted'
                        }`}
                      >
                        {testCaseAnalysis.coreStatus}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-terminal-muted whitespace-nowrap">
                        {t.form.edgeCases}:
                      </span>
                      <span
                        className={`text-right ${
                          testCaseAnalysis.edgeStatusType === 'verified'
                            ? 'text-status-go'
                            : testCaseAnalysis.edgeStatusType === 'failed'
                              ? 'text-status-danger'
                              : testCaseAnalysis.edgeStatusType === 'partial'
                                ? 'text-status-warning'
                                : 'text-terminal-muted'
                        }`}
                      >
                        {testCaseAnalysis.edgeStatus}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* 파일 미리보기 모달 */}
      {previewState.isOpen && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 backdrop-blur-sm"
          onClick={closePreview}
        >
          <div
            className="bg-terminal-bg border border-terminal-border rounded-xl shadow-2xl flex flex-col overflow-hidden w-[90vw] h-[85vh] max-w-[1200px]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* 미리보기 헤더 */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-terminal-border bg-terminal-dim/20">
              <div className="flex items-center gap-2">
                <span className="text-lg">
                  {previewState.fileType === 'csv' ? '📊' :
                   previewState.fileType === 'text' ? '📄' :
                   previewState.fileType === 'pdf' ? '📑' :
                   previewState.fileType === 'image' ? '🖼️' : '📁'}
                </span>
                <div>
                  <h3 className="text-sm font-bold text-terminal-text truncate max-w-[50vw]">
                    {previewState.fileName}
                  </h3>
                  <p className="text-xs text-terminal-muted">
                    {lang === 'ko' ? '미리보기' : 'Preview'}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={closePreview}
                className="p-1.5 hover:bg-terminal-dim/50 rounded-lg transition-colors text-terminal-muted hover:text-terminal-text"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* 미리보기 콘텐츠 */}
            <div className="flex-1 overflow-auto p-4">
              {previewState.fileType === 'csv' && previewState.content ? (
                <div className="overflow-auto h-full">
                  <table className="min-w-full border-collapse text-sm">
                    <thead className="sticky top-0 bg-terminal-bg z-10">
                      {parseCsv(previewState.content).slice(0, 1).map((row, rowIdx) => (
                        <tr key={rowIdx}>
                          {row.map((cell, cellIdx) => (
                            <th 
                              key={cellIdx}
                              className="px-2 py-1.5 text-left font-semibold text-terminal-text bg-terminal-dim/40 border border-terminal-border whitespace-nowrap text-xs"
                            >
                              {cell}
                            </th>
                          ))}
                        </tr>
                      ))}
                    </thead>
                    <tbody>
                      {parseCsv(previewState.content).slice(1).map((row, rowIdx) => (
                        <tr key={rowIdx} className="hover:bg-terminal-dim/20">
                          {row.map((cell, cellIdx) => (
                            <td 
                              key={cellIdx}
                              className="px-2 py-1.5 text-terminal-text border border-terminal-border max-w-[300px] truncate text-xs"
                              title={cell}
                            >
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : previewState.fileType === 'text' && previewState.content ? (
                <pre className="p-3 bg-terminal-dim/20 rounded-lg border border-terminal-border text-terminal-text text-xs font-mono whitespace-pre-wrap overflow-auto h-full">
                  {previewState.content}
                </pre>
              ) : previewState.fileType === 'pdf' && previewState.blobUrl ? (
                <object
                  data={previewState.blobUrl}
                  type="application/pdf"
                  className="w-full h-full rounded-lg border border-terminal-border"
                >
                  <div className="h-full flex items-center justify-center text-terminal-muted">
                    <a
                      href={previewState.blobUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 py-2 bg-status-go text-white rounded-lg hover:bg-status-go/90"
                    >
                      {lang === 'ko' ? '새 탭에서 열기' : 'Open in new tab'}
                    </a>
                  </div>
                </object>
              ) : previewState.fileType === 'image' && previewState.blobUrl ? (
                <div className="h-full flex items-center justify-center">
                  <img
                    src={previewState.blobUrl}
                    alt={previewState.fileName}
                    className="max-w-full max-h-full object-contain rounded-lg border border-terminal-border"
                  />
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default QAStatusSection;

